import { useState, useMemo } from "react";
import { TruckIcon, PackagePlus, Clock, Pencil, Trash2 } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { formatRupiah, formatTanggal } from "@/lib/format";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function StokMasuk() {
  const { products, restocks, addRestock, updateRestock, deleteRestock } = useShop();
  const [productId, setProductId] = useState("");
  const [jumlah, setJumlah] = useState("");
  const [hargaBeli, setHargaBeli] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [editJumlah, setEditJumlah] = useState("");
  const [editHarga, setEditHarga] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const selected = useMemo(
    () => products.find((p) => p.id === productId),
    [products, productId]
  );

  const onSelect = (id) => {
    setProductId(id);
    const p = products.find((x) => x.id === id);
    setHargaBeli(p ? String(p.harga_beli) : "");
  };

  const inputCls =
    "w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-900";

  const submit = async () => {
    if (!productId) {
      toast.error("Pilih barang terlebih dahulu");
      return;
    }
    const jml = Number(jumlah);
    if (!jml || jml <= 0) {
      toast.error("Jumlah harus lebih dari 0");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        product_id: productId,
        jumlah: jml,
        harga_beli: hargaBeli === "" ? null : Number(hargaBeli),
      };
      const res = await addRestock(payload);
      toast.success(`Stok ${res.nama} bertambah ${jml}`);
      setJumlah("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menambah stok");
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (r) => {
    setEditRec(r);
    setEditJumlah(String(r.jumlah));
    setEditHarga(String(r.harga_beli));
  };

  const submitEdit = async () => {
    const jml = Number(editJumlah);
    if (!jml || jml <= 0) {
      toast.error("Jumlah harus lebih dari 0");
      return;
    }
    try {
      await updateRestock(editRec.id, {
        jumlah: jml,
        harga_beli: editHarga === "" ? null : Number(editHarga),
      });
      toast.success("Catatan stok masuk diperbarui");
      setEditRec(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memperbarui");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteRestock(toDelete.id);
      toast.success("Catatan stok masuk dihapus");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus");
    }
    setToDelete(null);
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="px-8 pt-8 pb-4">
        <h2 className="font-heading font-bold text-2xl text-slate-900">Stok Masuk</h2>
        <p className="text-sm text-slate-500 mt-1">Catat pembelian barang dari supplier, stok bertambah otomatis</p>
      </div>

      <div className="px-8 pb-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <PackagePlus className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-heading font-bold text-slate-900">Tambah Stok Masuk</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Barang</label>
                <Select value={productId} onValueChange={onSelect}>
                  <SelectTrigger data-testid="restock-select-produk" className="mt-1 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Pilih barang..." />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`restock-option-${p.id}`}>
                        {p.nama} (stok {p.stok})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selected && (
                <div className="bg-slate-50 rounded-lg px-4 py-3 text-sm flex justify-between">
                  <span className="text-slate-500">Stok saat ini</span>
                  <span className="font-bold text-slate-900" data-testid="restock-stok-saat-ini">{selected.stok}</span>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-700">Jumlah Masuk</label>
                <input
                  data-testid="restock-jumlah"
                  type="number"
                  value={jumlah}
                  onChange={(e) => setJumlah(e.target.value)}
                  placeholder="0"
                  className={`${inputCls} mt-1`}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Harga Beli / pcs (opsional)</label>
                <input
                  data-testid="restock-harga-beli"
                  type="number"
                  value={hargaBeli}
                  onChange={(e) => setHargaBeli(e.target.value)}
                  placeholder="Kosongkan jika tidak berubah"
                  className={`${inputCls} mt-1`}
                />
                <p className="text-xs text-slate-400 mt-1">Isi jika harga modal berubah, akan memperbarui harga beli barang.</p>
              </div>

              <button
                data-testid="btn-simpan-stok-masuk"
                onClick={submit}
                disabled={submitting}
                className="w-full bg-blue-600 text-white hover:bg-blue-700 font-semibold rounded-xl px-6 py-3.5 transition-colors active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <TruckIcon className="w-5 h-5" /> Simpan Stok Masuk
              </button>
            </div>
          </div>
        </div>

        {/* History */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-400" />
              <h3 className="font-heading font-bold text-slate-900">Riwayat Stok Masuk</h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-left py-3.5 px-5">Barang</th>
                  <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-center py-3.5 px-5">Jumlah</th>
                  <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-right py-3.5 px-5">Harga Beli</th>
                  <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-right py-3.5 px-5">Total</th>
                  <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-right py-3.5 px-5">Waktu</th>
                  <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-right py-3.5 px-5">Aksi</th>
                </tr>
              </thead>
              <tbody data-testid="tabel-stok-masuk">
                {restocks.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`restock-row-${r.id}`}>
                    <td className="py-4 px-5 font-semibold text-slate-900">{r.nama}</td>
                    <td className="py-4 px-5 text-center">
                      <span className="font-bold text-emerald-600">+{r.jumlah}</span>
                    </td>
                    <td className="py-4 px-5 text-right text-slate-600">{formatRupiah(r.harga_beli)}</td>
                    <td className="py-4 px-5 text-right font-medium text-slate-900">{formatRupiah(r.total)}</td>
                    <td className="py-4 px-5 text-right text-xs text-slate-500">{formatTanggal(r.created_at)}</td>
                    <td className="py-4 px-5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`btn-edit-restock-${r.id}`}
                          onClick={() => openEdit(r)}
                          aria-label="Edit stok masuk"
                          className="w-9 h-9 rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center justify-center"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`btn-hapus-restock-${r.id}`}
                          onClick={() => setToDelete(r)}
                          aria-label="Hapus stok masuk"
                          className="w-9 h-9 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors flex items-center justify-center"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {restocks.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <TruckIcon className="w-12 h-12 mb-3" strokeWidth={1.2} />
                <p>Belum ada catatan stok masuk</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editRec} onOpenChange={() => setEditRec(null)}>
        <DialogContent className="max-w-sm" data-testid="dialog-edit-restock">
          <DialogHeader>
            <DialogTitle className="font-heading">Ubah Stok Masuk</DialogTitle>
          </DialogHeader>
          {editRec && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 rounded-lg px-4 py-3 text-sm">
                <span className="text-slate-500">Barang: </span>
                <span className="font-semibold text-slate-900">{editRec.nama}</span>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Jumlah Masuk</label>
                <input
                  data-testid="edit-restock-jumlah"
                  type="number"
                  value={editJumlah}
                  onChange={(e) => setEditJumlah(e.target.value)}
                  className={`${inputCls} mt-1`}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Harga Beli / pcs</label>
                <input
                  data-testid="edit-restock-harga"
                  type="number"
                  value={editHarga}
                  onChange={(e) => setEditHarga(e.target.value)}
                  className={`${inputCls} mt-1`}
                />
              </div>
              <p className="text-xs text-slate-400">Stok barang akan disesuaikan otomatis dengan perubahan jumlah.</p>
            </div>
          )}
          <DialogFooter>
            <button
              onClick={() => setEditRec(null)}
              className="bg-slate-100 text-slate-700 hover:bg-slate-200 font-medium rounded-lg px-5 py-2.5 transition-colors"
            >
              Batal
            </button>
            <button
              data-testid="btn-simpan-edit-restock"
              onClick={submitEdit}
              className="bg-blue-600 text-white hover:bg-blue-700 font-semibold rounded-lg px-5 py-2.5 transition-colors active:scale-95"
            >
              Simpan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}>
        <AlertDialogContent data-testid="dialog-hapus-restock">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus catatan stok masuk?</AlertDialogTitle>
            <AlertDialogDescription>
              Stok "{toDelete?.nama}" akan dikurangi kembali sebanyak {toDelete?.jumlah}. Aksi ini tidak bisa dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-konfirmasi-hapus-restock" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
